import { useState, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { profileQueries, type ProfileSection } from './queries';
import { profileData } from './profileData';
import type { ProfileData, ProfileEntity, ProfileValue } from './types';

export const ProfileRecordPager = ({ odrnumber, section, initial, history = false, filters, children }: {
  odrnumber: number; section: ProfileSection; initial: ProfileEntity; history?: boolean;
  filters?: ProfileValue[];
  children: (entity: ProfileEntity) => ReactNode;
}) => {
  const [offset, setOffset] = useState(0);
  const result = useQuery<Record<string, ProfileData>>(profileQueries[section.key], {
    variables: { odrnumber, entity: initial.name, history, offset, ...(filters?.length ? { filters } : {}) }, skip: offset === 0, fetchPolicy: 'network-only',
  });
  const data = profileData(result.data?.[`participantProfile${section.key}`], odrnumber);
  const entity = offset === 0 ? initial : data?.entities?.find(entity => entity.name === initial.name);
  const loading = offset > 0 && result.loading;
  const error = offset > 0 ? result.error?.message : null;
  const id = `profile-${history ? 'history-' : ''}${initial.name}-page`;
  return <div className="space-y-3">
    {loading && <p role="status">Loading records…</p>}
    {error && <p role="alert">{error}</p>}
    {offset > 0 && data?.blocked && <p role="alert">These records are unavailable for this participant.</p>}
    {offset > 0 && !loading && !error && !data?.blocked && !entity && <p role="alert">These records are unavailable.</p>}
    {!loading && !error && !data?.blocked && entity && children(entity)}
    {(initial.hasMore || offset > 0) && <div className="flex items-center justify-end gap-2">
      <Button id={`${id}-previous`} variant="outline" disabled={offset === 0 || loading} onClick={() => setOffset(current => Math.max(0, current - 100))}><ArrowLeft aria-hidden="true" className="mr-2 h-4 w-4" />Previous records</Button>
      <Button id={`${id}-next`} variant="outline" disabled={!entity?.hasMore || loading || Boolean(error)} onClick={() => setOffset(entity?.next ?? offset + 100)}><ArrowRight aria-hidden="true" className="mr-2 h-4 w-4" />Next records</Button>
    </div>}
  </div>;
};
